import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { TEMPLATE_MAP } from '../lib/categories';
import { updateAnalysis, deleteAnalysis } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Trash2 } from 'lucide-react';

/**
 * doc: { id, templateKey, title, status, raw }
 * All analyses now use the generic /api/analyses endpoints.
 */
const AnalysisEditorDialog = ({ open, onOpenChange, doc, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const template = doc ? TEMPLATE_MAP[doc.templateKey] : null;

  useEffect(() => {
    if (open && doc) {
      setForm({
        title: doc.raw.title,
        owner_email: doc.raw.owner_email,
        status: doc.raw.status,
        ...(doc.raw.data || {}),
      });
    }
  }, [open, doc]);

  if (!doc || !template) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      const { title, owner_email, status, ...data } = form;
      await updateAnalysis(doc.id, { title, owner_email, status, data });
      toast({ title: 'Saved', description: template.label });
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this analysis?')) return;
    try {
      await deleteAnalysis(doc.id);
      toast({ title: 'Deleted' });
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold"
              style={{ background: template.color }}
            >
              {template.label.split(' ').map((w) => w[0]).slice(0, 2).join('')}
            </div>
            {template.label}
          </DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Title</Label>
            <Input
              value={form.title || ''}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Owner email</Label>
            <Input
              type="email"
              value={form.owner_email || ''}
              onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
              className="mt-1.5"
            />
          </div>
        </div>

        <div>
          <Label>Status</Label>
          <Select
            value={form.status || 'draft'}
            onValueChange={(v) => setForm({ ...form, status: v })}
          >
            <SelectTrigger className="mt-1.5 max-w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {template.fields.map((field) => (
            <div key={field.name}>
              <Label>{field.label}</Label>
              {field.type === 'textarea' ? (
                <Textarea
                  value={form[field.name] || ''}
                  onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                  rows={3}
                  className="mt-1.5"
                />
              ) : (
                <Input
                  type={field.type}
                  value={form[field.name] || ''}
                  onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                  className="mt-1.5"
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleDelete}
            className="text-red-500 border-red-200 hover:bg-red-50 mr-auto"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisEditorDialog;
